import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { MDMonogram, AlertBanner } from "@/components/ui/system-primitives";
import { KeyRound } from "lucide-react";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import { toast } from "sonner";
import { nextLoginQuote } from "@/data/loginQuotes";
import { useRecaptcha } from "@/hooks/useRecaptcha";

// Mirrors the real page's split layout below so there's no layout jump
// once the session check resolves and the actual form swaps in.
function LoginSkeleton() {
    return (
        <div className="min-h-screen flex flex-col" data-testid="login-skeleton">
            <div className="flex-1 grid md:grid-cols-2">
                <div className="hidden md:block relative bg-muted">
                    <Skeleton className="w-full h-full rounded-none" />
                </div>
                <div className="flex items-center justify-center p-8">
                    <div className="w-full max-w-sm space-y-4">
                        <Skeleton className="h-8 w-40 mb-8" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-9 w-64" />
                        <Skeleton className="h-5 w-56 mb-4" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-11 w-full" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Login() {
    const { login, error, setError, refresh, beginGoogle, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();
    const loc = useLocation();
    const recaptcha = useRecaptcha();
    // Rotate the hero quote on every visit — persisted in localStorage
    // so consecutive login attempts see the next quote in the sequence.
    const quote = useMemo(() => nextLoginQuote(), []);

    async function submit(e) {
        e.preventDefault();
        setError(null); setBusy(true);
        // reCAPTCHA v3 — invisible bot score. Backend fails open if unset.
        const rcToken = await recaptcha.execute("login");
        const ok = await login(email, password, rcToken);
        setBusy(false);
        if (ok) {
            toast.success("Karibu! You're logged in.");
            const me = await refresh();
            if (me?.role === "admin") nav("/admin");
            else nav(loc.state?.from || "/app");
        }
    }

    // `user === null` is AuthContext's initial "still checking the session
    // cookie" state (as opposed to `false` = confirmed guest). Keep the page
    // static/skeleton until that settles instead of flashing the full form.
    if (user === null) return <LoginSkeleton />;

    return (
        <div className="min-h-screen flex flex-col">
            <div className="flex-1 grid md:grid-cols-2">
                <div className="hidden md:block relative">
                    <img
                        src="https://customer-assets-v7afamib.emergentagent.net/job_copy-trade-hub-11/artifacts/7vgua86a_gig-economy-african-women-cindy-sally.jpg"
                        alt="Melian Dialogue trader" loading="eager"
                        className="w-full h-full object-cover"
                        data-testid="login-hero-image"
                    />
                    <div className="absolute inset-0 bg-primary/20 backdrop-blur-[1px]" />
                    <div className="absolute bottom-8 left-8 right-8 backdrop-blur-md bg-background/70 p-6 rounded border border-border max-w-md" data-testid="login-quote-card">
                        <div className="overline text-primary mb-2" data-testid="login-quote-author">{quote.author}</div>
                        <div className="text-2xl font-bold leading-tight tracking-tight" data-testid="login-quote-text">
                            &ldquo;{quote.text}&rdquo;
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-center p-8">
                    <div className="w-full max-w-sm">
                        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-logo">
                            <MDMonogram size="md" testid="login-monogram" />
                            <div className="font-heading font-extrabold tracking-tight text-lg">Melian&nbsp;Dialogue<span className="text-primary">.</span></div>
                        </Link>
                        <div className="overline mb-3 text-primary" data-testid="login-eyebrow">Welcome back</div>
                        <h1 className="font-heading text-h2 mb-2" data-testid="login-h1">
                            Karibu <span className="text-primary">tena</span>.
                        </h1>
                        <p className="font-body text-body-lg text-muted-foreground mb-6">
                            Log in to your Melian Dialogue terminal.
                        </p>

                        <button
                            type="button"
                            onClick={beginGoogle}
                            data-testid="login-google"
                            className="w-full mb-4 inline-flex items-center justify-center gap-2 border border-border bg-card hover:bg-secondary text-foreground rounded-md py-2.5 text-sm font-semibold transition-colors"
                        >
                            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"/>
                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                            </svg>
                            Continue with Google
                        </button>
                        <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                                <span className="bg-background px-2 text-muted-foreground">or with email</span>
                            </div>
                        </div>
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Password</Label>
                                    <Link
                                        to="/recover-with-codes"
                                        className="text-xs text-muted-foreground hover:text-primary"
                                        data-testid="login-forgot-password"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password" toggleTestId="login-password-toggle" autoComplete="current-password" />
                            </div>
                            {error && <div className="text-danger text-sm" data-testid="login-error">{error}</div>}
                            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
                                {busy ? "Signing in…" : "Sign in"}
                            </Button>
                            <div className="text-xs text-muted-foreground">
                                No account? <Link to="/register" className="text-primary hover:underline" data-testid="login-goto-register">Create free account</Link>
                            </div>
                        </form>

                        {/* Closed-loop recovery hint. Uses the shared AlertBanner
                            (warning variant) so this notice reads identical to every
                            other cautionary banner across the platform. */}
                        <div className="mt-6" data-testid="login-recovery-hint">
                            <AlertBanner variant="warning" icon={KeyRound} title="Locked out?" testid="login-recovery-alert">
                                This platform is closed-loop &mdash; we don&rsquo;t send password-reset emails.
                                Two ways back in:{" "}
                                <button
                                    type="button"
                                    onClick={beginGoogle}
                                    data-testid="login-recovery-google"
                                    className="font-heading font-medium underline underline-offset-2"
                                >
                                    continue with Google
                                </button>{" "}or{" "}
                                <Link to="/recover-with-codes"
                                      className="font-heading font-medium underline underline-offset-2"
                                      data-testid="login-recovery-codes-link">
                                    use one of your 10 recovery codes
                                </Link>. Lost every code?{" "}
                                <Link to="/contact"
                                      className="font-heading font-medium underline underline-offset-2"
                                      data-testid="login-recovery-contact">
                                    Contact support &rarr;
                                </Link>
                            </AlertBanner>
                        </div>
                    </div>
                </div>
            </div>
            <ComplianceFooter />
        </div>
    );
}
